import { useEffect, useMemo, useState, type JSX } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import type {
  AdminUserCreate,
  AdminUserDto,
  AdminUserUpdate,
  UserRole,
} from '@/api/types/AdminUser';
import { USER_ROLES } from '@/api/types/AdminUser';
import { BrandChip, StatusChip, type StatusKey } from '@/components/shell';
import { DialogSubmitOverlay, SubmitButton } from '@/components/loading';
import { useAppDispatch } from '@/store/hooks';
import {
  activateUserAndInvalidate,
  createUserAndInvalidate,
  deactivateUserAndInvalidate,
  resendInviteAndInvalidate,
  updateUserAndInvalidate,
} from '@/store/adminUsersSlice';

// E.164 — leading '+' and 8-15 digits. Server re-validates; FE catches the
// obvious typos (whitespace, missing '+', too short) before the admin has
// to round-trip to Keycloak for "invalid phone".
const E164_RE = /^\+\d{8,15}$/;

const NEW_USER_SUB = '__new__';

// User state → shared StatusChip palette key (mirrors AdminUsersPage). The
// edit-modal header shows the current state as a §6.4 dot chip (mockup
// `#screen-user-edit` `.chip.st-done.dot` = aktivní).
const STATE_STATUS_KEY: Record<
  AdminUserDto['state'],
  StatusKey
> = {
  AKTIVNI: 'active',
  POZVANY: 'waiting',
  DEAKTIVOVANY: 'inactive',
};

interface FormShape {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  roles: UserRole[];
  notificationsEnabled: boolean;
}

const emptyForm: FormShape = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  roles: [],
  // CHR-03 §1: new users default to notifications ON.
  notificationsEnabled: true,
};

const dtoToForm = (u: AdminUserDto): FormShape => ({
  email: u.email,
  firstName: u.firstName,
  lastName: u.lastName,
  phoneNumber: u.phoneNumber ?? '',
  roles: [...u.roles],
  notificationsEnabled: u.notificationsEnabled,
});

export interface AdminUserEditorProps {
  // `null` → editor closed. `'__new__'` → create. Any other string → existing
  // user sub being edited; `existing` MUST be populated by the parent.
  sub: string | null;
  existing: AdminUserDto | null;
  onClose: () => void;
  onSaved: (saved: AdminUserDto, isCreate: boolean) => void;
  onDeactivated: (updated: AdminUserDto) => void;
  onActivated: (updated: AdminUserDto) => void;
  onInviteResent: () => void;
}

export const AdminUserEditor = ({
  sub,
  existing,
  onClose,
  onSaved,
  onDeactivated,
  onActivated,
  onInviteResent,
}: AdminUserEditorProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const dispatch = useAppDispatch();

  const isCreate = sub === NEW_USER_SUB;
  const isOpen = sub !== null;

  // Schema is rebuilt only when create-vs-edit toggles — yup transforms email
  // into "not required & nullable" in edit mode since the field is immutable.
  const schema = useMemo(
    () =>
      yup.object({
        email: isCreate
          ? yup
              .string()
              .required(t('validation.required'))
              .email(t('validation.email'))
          : yup.string().defined(),
        firstName: yup
          .string()
          .required(t('validation.required'))
          .trim()
          .min(1, t('validation.required')),
        lastName: yup
          .string()
          .required(t('validation.required'))
          .trim()
          .min(1, t('validation.required')),
        phoneNumber: yup
          .string()
          .required(t('validation.required'))
          .matches(E164_RE, t('validation.phone')),
        roles: yup
          .array()
          .of(yup.mixed<UserRole>().oneOf(USER_ROLES as readonly UserRole[]).required())
          .min(1, t('validation.rolesRequired'))
          .required(t('validation.rolesRequired')),
        // Boolean toggle — always defined (default true). Kept in the schema
        // so yupResolver does not strip it from the resolved submit values
        // (it MUST round-trip into the full-replace PATCH payload).
        notificationsEnabled: yup.boolean().required(),
      }),
    [isCreate, t],
  );

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormShape>({
    defaultValues: emptyForm,
    resolver: yupResolver(schema) as never,
  });

  const [saveError, setSaveError] = useState<string | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [resending, setResending] = useState(false);

  // Re-seed the form whenever the dialog target changes.
  useEffect(() => {
    if (sub === null) return;
    setSaveError(null);
    reset(isCreate ? emptyForm : existing ? dtoToForm(existing) : emptyForm);
  }, [sub, isCreate, existing, reset]);

  const onSubmit = async (values: FormShape): Promise<void> => {
    if (submitting) return;
    setSaveError(null);
    setSubmitting(true);
    try {
      if (isCreate) {
        const payload: AdminUserCreate = {
          email: values.email.trim(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          phoneNumber: values.phoneNumber.trim(),
          roles: values.roles,
        };
        const saved = await dispatch(createUserAndInvalidate(payload)).unwrap();
        onSaved(saved, true);
      } else if (existing) {
        const payload: AdminUserUpdate = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          phoneNumber: values.phoneNumber.trim(),
          roles: values.roles,
          // ⚠ ALWAYS send the current value — the PATCH is full-replace, so an
          // omitted field defaults back to true server-side and would silently
          // re-enable a disabled user (CHR-03 §1 / ADR-0045).
          notificationsEnabled: values.notificationsEnabled,
        };
        const saved = await dispatch(
          updateUserAndInvalidate({ sub: existing.sub, payload }),
        ).unwrap();
        onSaved(saved, false);
      }
    } catch (e) {
      setSaveError(
        isCreate ? t('toast.createFailed') : t('toast.updateFailed'),
      );
      // Re-throw so the caller may inspect; here we just keep the dialog open.
      void e;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (): Promise<void> => {
    if (!existing || deactivating) return;
    setDeactivating(true);
    try {
      const updated = await dispatch(
        deactivateUserAndInvalidate(existing.sub),
      ).unwrap();
      setShowDeactivateConfirm(false);
      onDeactivated(updated);
    } catch {
      setSaveError(t('toast.deactivateFailed'));
      setShowDeactivateConfirm(false);
    } finally {
      setDeactivating(false);
    }
  };

  // Reactivation is non-destructive (KC enabled=true) — no confirm dialog,
  // mirroring the row action and unlike the destructive deactivate path.
  const handleActivate = async (): Promise<void> => {
    if (!existing || activating) return;
    setActivating(true);
    try {
      const updated = await dispatch(
        activateUserAndInvalidate(existing.sub),
      ).unwrap();
      onActivated(updated);
    } catch {
      setSaveError(t('toast.activateFailed'));
    } finally {
      setActivating(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (!existing || resending) return;
    setResending(true);
    try {
      await dispatch(resendInviteAndInvalidate(existing.sub)).unwrap();
      onInviteResent();
    } catch {
      setSaveError(t('toast.inviteFailed'));
    } finally {
      setResending(false);
    }
  };

  const titleStr = isCreate
    ? t('modal.titleCreate')
    : existing
    ? t('modal.titleEdit', {
        name: `${existing.firstName} ${existing.lastName}`.trim(),
      })
    : t('modal.titleEditNoName');

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        data-testid="admin-user-editor"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 1.5,
          }}
        >
          <Box component="span" sx={{ fontSize: 17, fontWeight: 600 }}>
            {titleStr}
          </Box>
          {!isCreate && existing ? (
            <Typography
              variant="caption"
              sx={{
                fontFamily: theme.monospaceFontFamily,
                color: theme.palette.text.secondary,
              }}
            >
              sub: {existing.sub.slice(0, 8)}…
            </Typography>
          ) : null}
          {!isCreate && existing ? (
            <StatusChip
              status={STATE_STATUS_KEY[existing.state]}
              label={t(`state.${existing.state}`)}
              data-testid="editor-state-chip"
            />
          ) : null}
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={onClose}
            aria-label={t('modal.close')}
            sx={{ width: 44, height: 44 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={submitting} />
          <Stack
            component="form"
            id="admin-user-editor-form"
            onSubmit={handleSubmit(onSubmit)}
            spacing={2.5}
            sx={{ pt: 1 }}
          >
            {saveError ? (
              <Alert severity="error" data-testid="editor-save-error">
                {saveError}
              </Alert>
            ) : null}

            <TextField
              {...register('email')}
              type="email"
              label={t('field.email')}
              helperText={
                errors.email?.message ??
                (isCreate ? t('field.emailSubCreate') : t('field.emailSubEdit'))
              }
              error={Boolean(errors.email)}
              disabled={!isCreate}
              fullWidth
              size="small"
              InputProps={
                !isCreate
                  ? {
                      sx: {
                        bgcolor: theme.palette.background.default,
                        color: theme.palette.text.secondary,
                      },
                    }
                  : undefined
              }
              inputProps={{ 'data-testid': 'admin-user-email' }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                {...register('firstName')}
                label={t('field.firstName')}
                helperText={errors.firstName?.message ?? ' '}
                error={Boolean(errors.firstName)}
                fullWidth
                size="small"
                inputProps={{ 'data-testid': 'admin-user-firstname' }}
              />
              <TextField
                {...register('lastName')}
                label={t('field.lastName')}
                helperText={errors.lastName?.message ?? ' '}
                error={Boolean(errors.lastName)}
                fullWidth
                size="small"
                inputProps={{ 'data-testid': 'admin-user-lastname' }}
              />
            </Stack>

            <TextField
              {...register('phoneNumber')}
              type="tel"
              label={t('field.phone')}
              placeholder={t('field.phoneHint')}
              helperText={errors.phoneNumber?.message ?? t('field.phoneSub')}
              error={Boolean(errors.phoneNumber)}
              fullWidth
              size="small"
              InputProps={{
                sx: {
                  fontFamily: theme.monospaceFontFamily,
                  fontFeatureSettings: '"tnum"',
                },
              }}
              inputProps={{ 'data-testid': 'admin-user-phone' }}
            />

            <Controller
              name="roles"
              control={control}
              render={({ field }) => {
                const selected = field.value;
                const remove = (r: UserRole): void => {
                  field.onChange(selected.filter((s) => s !== r));
                };
                return (
                  <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(errors.roles)}
                  >
                    <InputLabel id="admin-user-roles-label">
                      {t('field.roles')}
                    </InputLabel>
                    <Select<UserRole[]>
                      labelId="admin-user-roles-label"
                      multiple
                      value={selected}
                      label={t('field.roles')}
                      onChange={(e) => {
                        const v = e.target.value;
                        field.onChange(
                          typeof v === 'string'
                            ? (v.split(',') as UserRole[])
                            : v,
                        );
                      }}
                      data-testid="admin-user-roles-select"
                      renderValue={(picked) => (
                        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
                          {picked.map((r) => (
                            <Chip
                              key={r}
                              size="small"
                              label={t(`role.${r}`)}
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={(e) => {
                                e.stopPropagation();
                                remove(r);
                              }}
                              sx={{
                                fontSize: 11,
                                fontWeight: 500,
                                bgcolor: theme.palette.primary.light,
                                color: theme.palette.primary.main,
                                '& .MuiChip-deleteIcon': {
                                  color: theme.palette.primary.main,
                                  '&:hover': { color: theme.palette.primary.dark },
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    >
                      {USER_ROLES.map((r) => (
                        <MenuItem key={r} value={r} data-testid={`role-option-${r}`}>
                          <Checkbox checked={selected.includes(r)} />
                          <ListItemText primary={t(`role.${r}`)} />
                          <Box sx={{ ml: 1, display: 'inline-flex' }}>
                            <BrandChip variant={selected.includes(r) ? 'brand' : 'muted'}>
                              {t(`role.${r}`)}
                            </BrandChip>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.roles?.message ?? t('field.rolesSub')}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />

            {!isCreate && existing ? (
              <Controller
                name="notificationsEnabled"
                control={control}
                render={({ field }) => (
                  <FormControl
                    component="fieldset"
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('field.notifications')}
                      </Typography>
                      <FormHelperText sx={{ m: 0 }}>
                        {t('field.notificationsSub')}
                      </FormHelperText>
                    </Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: field.value
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          fontWeight: 600,
                        }}
                      >
                        {t(
                          field.value
                            ? 'field.notificationsOn'
                            : 'field.notificationsOff',
                        )}
                      </Typography>
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        slotProps={{
                          input: {
                            'aria-label': t('field.notifications'),
                            // data-* is valid on the underlying <input> but not
                            // in the MUI slot-prop type; cast to attach the hook.
                            ...({ 'data-testid': 'admin-user-notifications' } as Record<
                              string,
                              string
                            >),
                          },
                        }}
                      />
                    </Stack>
                  </FormControl>
                )}
              />
            ) : null}

            {!isCreate && existing ? (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    {t('field.actions')} — {t('field.actionsSub')}
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {existing.state === 'POZVANY' ? (
                      <SubmitButton
                        variant="outlined"
                        color="warning"
                        loading={resending}
                        onClick={() => void handleResend()}
                        data-testid="editor-resend-invite"
                        sx={{ minHeight: 44 }}
                      >
                        {t('action.resendInvite')}
                      </SubmitButton>
                    ) : null}
                    {existing.state === 'DEAKTIVOVANY' ? (
                      <SubmitButton
                        variant="contained"
                        color="success"
                        loading={activating}
                        onClick={() => void handleActivate()}
                        data-testid="editor-activate"
                        sx={{ minHeight: 44 }}
                      >
                        {t('action.activate')}
                      </SubmitButton>
                    ) : (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => setShowDeactivateConfirm(true)}
                        data-testid="editor-deactivate"
                        sx={{ minHeight: 44 }}
                      >
                        {t('action.deactivate')}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, flex: 1 }}
          >
            {isCreate ? t('modal.subtitleCreate') : t('modal.subtitleEdit')}
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              onClick={onClose}
              variant="outlined"
              disabled={submitting}
              sx={{ minHeight: 44 }}
            >
              {t('action.cancel')}
            </Button>
            <SubmitButton
              type="submit"
              form="admin-user-editor-form"
              variant="contained"
              loading={submitting || isSubmitting}
              data-testid="editor-save-button"
              sx={{ minHeight: 44, minWidth: 160 }}
            >
              {isCreate ? t('action.create') : t('action.save')}
            </SubmitButton>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        data-testid="deactivate-confirm"
      >
        <DialogTitle>{t('deactivate.confirmTitle')}</DialogTitle>
        <DialogContent sx={{ position: 'relative' }}>
          <DialogSubmitOverlay submitting={deactivating} />
          <Typography variant="body2">
            {t('deactivate.confirmBody', {
              name: existing
                ? `${existing.firstName} ${existing.lastName}`.trim()
                : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeactivateConfirm(false)}
            disabled={deactivating}
          >
            {t('deactivate.confirmNo')}
          </Button>
          <SubmitButton
            color="error"
            loading={deactivating}
            onClick={() => void handleDeactivate()}
          >
            {t('deactivate.confirmYes')}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
